import { call, cancel, fork, race, select, take } from 'redux-saga/effects';
import axios from 'axios';

// Global Selectors
// @ts-ignore
import { idTokenSelector } from 'services/auth/selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

/**
 * Forks a saga on each action dispatched to the Store that matches the start pattern.
 * And automatically cancels any previous saga task started previously if it's still running.
 * And cancels any tasks when an action is dispatched to the Store that matches the end pattern
 * @param startPattern - the action type
 * @param endPattern -
 * @param worker
 */
export function* takeLatestWithCancel(
    startPattern: string | string[],
    endPattern: string,
    worker: any
): any {
    let lastTask;
    while (true) {
        const tasks = yield race([take(startPattern), take(endPattern)]);
        if (lastTask) {
            yield cancel(lastTask);
        }
        if (tasks && tasks[0]) {
            lastTask = yield fork(worker, tasks[0]);
        }
    }
}

/**
 * Makes a post request with the API domain name attached to the path.
 * And with the Authorization header included.
 * @param path - the server URL path that will be used for the request
 * @param data - the data to be sent as the request body
 */
export function* postRequest(path: string, data?: Record<string, any>): any {
    const idToken = yield select(idTokenSelector);

    return yield call(axios.post, `${REACT_APP_API_URL}/v1/${path}`, data, {
        headers: {
            Authorization: idToken
        }
    });
}

/**
 * Makes a put request with the API domain name attached to the path.
 * And with the Authorization header included.
 * @param path - the server URL path that will be used for the request
 * @param data - the data to be sent as the request body
 */
export function* putRequest(path: string, data: Record<string, any>): any {
    const idToken = yield select(idTokenSelector);

    return yield call(axios.put, `${REACT_APP_API_URL}/v1/${path}`, data, {
        headers: {
            Authorization: idToken
        }
    });
}

/**
 * Makes a patch request with the API domain name attached to the path.
 * And with the Authorization header included.
 * @param path - the server URL path that will be used for the request
 * @param data - the data to be sent as the request body
 * @param id - the id to be patched
 */
export function* patchRequest(
    path: string,
    data: Record<string, any>,
    id: number
): any {
    const idToken = yield select(idTokenSelector);

    return yield call(
        axios.patch,
        `${REACT_APP_API_URL}/v1/${path}/${id}`,
        data,
        {
            headers: {
                Authorization: idToken
            }
        }
    );
}

/**
 * Makes a delete request with the API domain name attached to the path.
 * And with the Authorization header included.
 * @param path - the server URL path that will be used for the request
 * @param id - the id to be deleted
 */
export function* deleteRequest(path: string, id: number): any {
    const idToken = yield select(idTokenSelector);

    return yield call(axios.delete, `${REACT_APP_API_URL}/v1/${path}/${id}`, {
        headers: {
            Authorization: idToken
        }
    });
}
